/**
 * Static text keyboard layout definitions.
 * These are pure JS objects — no React, no hooks, no side effects.
 * Layouts that have dynamic bottom rows (textDefaultCaps, emailText) are
 * built by buildTextLayouts(props) so the dynamic row is isolated.
 */

export const TEXT_LAYOUTS = {
  text: {
    default: [
      '1 2 3 4 5 6 7 8 9 0',
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m {bksp}',
      '{alt} {space} {done}',
    ],
    shift: [
      '1 2 3 4 5 6 7 8 9 0',
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M {bksp}',
      '{alt} {space} {done}',
    ],
    alt: [
      '1 2 3 4 5 6 7 8 9 0',
      '@ # ₹ _ & - + ( ) /',
      `* " ' : ; ! ? % ^`,
      '= $ { } € 𝜋 [ ] {bksp}',
      '{default} , {space} . {done}',
    ],
  },

  textWithoutNumeric: {
    default: [
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m {bksp}',
      '{alt} @ {space} . .com',
    ],
    shift: [
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M {bksp}',
      '{alt} @ {space} . .com',
    ],
    alt: [
      '1 2 3 4 5 6 7 8 9 0',
      '- / : ; ( ) $ & ?',
      `# . , ! ' " + % {bksp}`,
      '{default} @ {space} . .com',
    ],
  },

  textWithNumericEmail: {
    default: [
      '1 2 3 4 5 6 7 8 9 0',
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{special} z x c v b n m {bksp}',
      '@ _ {space} . .com',
    ],
    shift: [
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M {bksp}',
      '{alt} @ {space} . .com',
    ],
    alt: [
      '1 2 3 4 5 6 7 8 9 0',
      '- / : ; ( )',
      '# , ! * = ?',
      `{lowercase} ^ ' " + % {bksp}`,
      '@ _ {space} . .com',
    ],
  },

  textWithOutDone: {
    default: [
      '1 2 3 4 5 6 7 8 9 0',
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m {bksp}',
      '{alt} {space} {return}',
    ],
    shift: [
      '1 2 3 4 5 6 7 8 9 0',
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M {bksp}',
      '{alt} {space} {return}',
    ],
    alt: [
      '1 2 3 4 5 6 7 8 9 0',
      '@ # ₹ _ & - + ( ) /',
      `* " ' : ; ! ? % ^`,
      '= $ { } € 𝜋 [ ] {bksp}',
      '{default} , {space} . {return}',
    ],
  },

  surveyText: {
    default: [
      '` 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
      'q w e r t y u i o p [ ] \\',
      "a s d f g h j k l ; ' {surveyBack}",
      '{shift} z x c v b n m , . / {shift}',
      '.com {alt} @ {space} {done}',
    ],
    shift: [
      '~ ! @ # $ % ^ & * ( ) _ + {bksp}',
      'Q W E R T Y U I O P { } |',
      'A S D F G H J K L : " {surveyBack}',
      '{shift} Z X C V B N M < > ? {shift}',
      '.com {alt} @ {space} {done}',
    ],
    alt: [
      '@ # & * - + = ( ) {bksp}',
      '1 2 3 4 5 6 7 8 9 0 \\',
      '% ^ ~ | [ ] < > { } {surveyBack}',
      '{shift} _ $ " , : ; . ? / {shift}',
      '.com {default} {space} {done}',
    ],
  },
};

/**
 * Build text layouts that require dynamic bottom rows.
 * @param {object} opts
 * @param {boolean} opts.isDone       — emailText: show {done} vs {return}
 * @param {boolean} opts.isOrderSeat
 * @param {boolean} opts.isOfflinePay
 * @param {boolean} opts.callApi
 * @param {boolean} opts.orderSearch
 */
export function buildDynamicTextLayouts({ isDone, isOrderSeat, isOfflinePay, callApi, orderSearch }) {
  // emailText bottom row depends on isDone
  const emailAction = isDone ? '{done}' : '{return}';
  const emailText = {
    default: [
      '1 2 3 4 5 6 7 8 9 0',
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m {bksp}',
      `{alt} .com {space} @ ${emailAction}`,
    ],
    shift: [
      '1 2 3 4 5 6 7 8 9 0',
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M {bksp}',
      `{alt} .com {space} @ ${emailAction}`,
    ],
    alt: [
      '1 2 3 4 5 6 7 8 9 0',
      '@ # ₹ _ & - + ( ) /',
      `* " ' : ; ! ? % ^`,
      '= $ { } € 𝜋 [ ] {bksp}',
      `{default} , {space} . ${emailAction}`,
    ],
  };

  // textDefaultCaps bottom row depends on several flags
  const capsBottomDefault =
    isOrderSeat || isOfflinePay
      ? callApi
        ? '{alt} @ {space} .'
        : '{alt} @ {space} .'
      : callApi
      ? '{alt} @ {space} .'
      : `{alt} @ {space} . ${orderSearch ? '{cancel}' : '{done}'}`;

  const capsBottomShift = callApi
    ? '{alt} @ {space} .'
    : `{alt} @ {space} . ${orderSearch ? '{cancel}' : '{done}'}`;

  const capsBottomAlt = isOrderSeat
    ? callApi
      ? '{default} @ {space} .'
      : '{default} @ {space} .'
    : callApi
    ? '{default} @ {space} .'
    : `{default} @ {space} . ${orderSearch ? '{cancel}' : '{done}'}`;

  const textDefaultCaps = {
    default: [
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M {bksp}',
      capsBottomDefault,
    ],
    shift: [
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m {bksp}',
      capsBottomShift,
    ],
    alt: [
      '1 2 3 4 5 6 7 8 9 0',
      '- / : ; ( ) $ & ?',
      `# , ! ' " + % {bksp}`,
      capsBottomAlt,
    ],
  };

  return { emailText, textDefaultCaps };
}
